import React, { useEffect, useRef, useState } from "react";

import { Box, CircularProgress, Tooltip, useTheme } from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { Link } from "react-router-dom";

import { useGetFilesFromChannel } from "@/api/useGetFilesFromChannel";
import { DocumentIcon } from "@/components/DocumentManagementView/DocumentGrid/DocumentIcon";
import CustomGrid from "@/components/shared/CustomGrid/CustomGrid";
import { CustomGridRef } from "@/components/shared/CustomGrid/types";
import SearchField from "@/components/shared/SearchField/SearchField";
import { useTeamsAndChannels } from "@/context/TeamsAndChannelsContext";
import { useExploreFolder } from "@/hooks/useExploreFolder";
import { useTeamsFiles } from "@/hooks/useTeamsFiles";
import DocumentGridBreadcrumb from "@/pages/DocumentManagementView/DocumentGridBreadcrumb";
import { TYPE_FILE, TYPE_FOLDER } from "@/util/constants";
import { TeamsFile } from "@/util/interfaces/TeamsFile";

interface TeamsDocGridProps {
  selectedChannel:
    | { drive_id: string; item_id: string; channelName: string; teamName: string }
    | undefined;
  onLoadingChange: (_loading: boolean) => void;
}

export const TeamsDocGrid: React.FC<TeamsDocGridProps> = ({ selectedChannel, onLoadingChange }) => {
  const [selectedPath, setSelectedPath] = useState<{ id: string; name: string }[]>([]);
  const [selectedPathIds, setSelectedPathIds] = useState<string[]>([]);
  const [parentId, setParentId] = useState<string>(selectedChannel ? selectedChannel.item_id : "");
  const [searchText, setSearchText] = useState<string>("");
  const theme = useTheme();
  const { msalConfig } = useTeamsAndChannels();
  const {
    addTeamsFiles,
    getTeamsFilesByParentId,
    selectedRows,
    setSelectedRows,
    updateTeamsFile,
    teamsFiles,
    getSelectedFiles,
  } = useTeamsFiles();
  const { exploreFolderInBackground, isLoading: isExploringFolders } = useExploreFolder();
  const { data: files, isLoading } = useGetFilesFromChannel(
    { drive_id: selectedChannel?.drive_id ?? "", item_id: parentId },
    msalConfig.backend_uri
  );
  const gridRef = useRef<CustomGridRef>(null);
  // Get grid rows using the custom hook
  const allGridRows = getTeamsFilesByParentId(parentId);

  // Apply search filter if there's search text
  const gridRows = searchText
    ? allGridRows.filter((file) => file.name.toLowerCase().includes(searchText.toLowerCase()))
    : allGridRows;
  useEffect(() => {
    if (selectedChannel) {
      setParentId(selectedChannel.item_id);
      setSelectedPath([]);
      setSelectedPathIds([]);
    }
  }, [selectedChannel]);

  useEffect(() => {
    if (files && files.length > 0 && selectedChannel) {
      addTeamsFiles(
        files.map((f) => ({
          ...f,
          channel: {
            ...selectedChannel,
          },
        }))
      );

      // Mark the parentId as explored
      if (parentId) {
        updateTeamsFile(parentId, { explored: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    files,
    parentId,
    selectedChannel?.item_id,
    selectedChannel?.drive_id,
    selectedChannel?.channelName,
    selectedChannel?.teamName,
  ]);
  useEffect(() => {
    if (selectedPath.length === 0) {
      setParentId(selectedChannel ? selectedChannel.item_id : "");
      setSelectedPathIds([]);
    } else {
      const pathLength = selectedPath.length;
      setParentId(selectedPathIds[pathLength - 1]);
      setSelectedPathIds(selectedPathIds.slice(0, pathLength));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPath]);

  // Auto-select children when new files are loaded for selected folders
  // Note: This logic is now mostly handled by SimpleGrid's hierarchical selection,
  // but we keep this to ensure folder exploration happens when needed
  useEffect(() => {
    if (selectedRows.length === 0 || teamsFiles.length === 0) return;

    // Check each selected row to see if it's a folder that needs exploration
    for (const selectedId of selectedRows) {
      const selectedFile = teamsFiles.find((f) => f.id === selectedId);
      if (selectedFile?.folder && !selectedFile.explored && selectedChannel) {
        exploreFolderInBackground(selectedFile, selectedChannel);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamsFiles.length]); // Trigger when teamsFiles count changes

  // Update submit button state when folder exploration status changes
  useEffect(() => {
    onLoadingChange(isExploringFolders);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExploringFolders]);

  const handleColumnNameClick = (teamsFile: TeamsFile) => {
    if (teamsFile.folder) {
      setParentId(teamsFile.id);
      setSelectedPathIds([...selectedPathIds, teamsFile.id]);
      setSelectedPath([...selectedPath, { id: teamsFile.id, name: teamsFile.name }]);
    }
  };

  const columns = [
    {
      field: "name",
      headerName: "Document Name",
      renderCell: (params: GridRenderCellParams) => {
        const itemType = params.row.folder ? TYPE_FOLDER : TYPE_FILE;
        return (
          <Tooltip title={params ? params.row.name : null} arrow>
            <Box className="flex align-items-center gap-8">
              <DocumentIcon fileType={itemType} />
              <Link
                style={{
                  color: itemType === TYPE_FOLDER ? theme.palette.primary.main : "#515152",
                  textDecoration: "none",
                  maxWidth: "80%",
                  cursor: itemType === TYPE_FOLDER ? "pointer" : "default",
                }}
                data-testid={`link-${params.row.name}`}
                className="text-overflow-ellipsis"
                to={`/document-viewer?id=${params.row.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  handleColumnNameClick(params.row);
                }}
                target="_blank"
              >
                {params.row.name}
              </Link>
            </Box>
          </Tooltip>
        );
      },
      flex: 1,
    },
    {
      field: "lastModifiedDateTime",
      headerName: "Modified",
      flex: 1,
    },
    {
      field: "lastModifiedBy",
      headerName: "Modified by",
      renderCell: (params: GridRenderCellParams) => (
        <>{params.row.lastModifiedBy.user.displayName}</>
      ),
      flex: 1,
    },
  ];

  // Handle folder exploration when a folder is selected
  const handleFolderSelection = (file: TeamsFile) => {
    if (file.folder && selectedChannel && !file.explored) {
      exploreFolderInBackground(file, selectedChannel);
    }
  };

  // Function to handle selection changes from SimpleGrid
  const handleCustomGridSelectionChange = (selectedRowsFromGrid: TeamsFile[]) => {
    // Handle folder exploration for newly selected folders
    for (const file of selectedRowsFromGrid) {
      if (file.folder && !selectedRows.includes(file.id)) {
        handleFolderSelection(file);
      }
    }

    // Update the Teams file selection state with the new selection
    setSelectedRows(selectedRowsFromGrid.map((row) => row.id));
  };
  const selectedFiles = getSelectedFiles().filter((f) => !f.folder);
  return (
    <Box height="100%">
      <Box className="flex gap-8 align-items-center grid-header" height="68px">
        <div className="flex space-between width-100">
          <Box maxWidth="250px">
            <SearchField
              searchText={searchText}
              setSearchText={setSearchText}
              placeholder="Search by Document Name"
            />
          </Box>
          {selectedFiles.length > 0 && (
            <div className="flex align-items-center">
              <div style={{ display: "flex", alignItems: "center", padding: "0 10px", gap: "5px" }}>
                {isExploringFolders && <CircularProgress size={12} />}
                <span>
                  <b>Selected files: </b>
                </span>
              </div>
              <span>
                {" "}
                {selectedFiles.length} (
                {Number(selectedFiles.reduce((acum, f) => acum + f.size, 0) / 1024 / 1024).toFixed(
                  2
                )}{" "}
                MB)
              </span>
            </div>
          )}
        </div>
      </Box>
      <DocumentGridBreadcrumb
        rootText={selectedChannel?.channelName}
        breadcrumbs={selectedPath}
        clickOnPathCallback={setSelectedPath}
      />
      <CustomGrid
        ref={gridRef}
        style={{
          height: "calc(100% - 68px - 35px)",
          maxHeight: "calc(100% - 68px - 35px)",
          overflow: "visible",
        }}
        loading={isLoading}
        rows={gridRows}
        columns={columns as unknown as GridColDef[]}
        checkboxSelection
        keepNonExistentRowsSelected
        allRows={teamsFiles}
        initialSelectedRowIds={selectedRows}
        onSelectionChange={handleCustomGridSelectionChange}
        pageSizeOptions={[5, 10, 50, 100]}
        disableRowSelectionOnClick
        classes={{
          footerContainer: "footer",
        }}
      />
    </Box>
  );
};
